import React, { useState } from "react";
import { toast } from "sonner";
import EstatePage from "@/components/sites/EstatePage";
import Reveal from "@/components/sites/Reveal";
import { useBrand, useTenantSlug } from "@/lib/brand";
import { api } from "@/lib/api";
import { addressLine, hoursLine } from "@/lib/agencyDetails";

/**
 * Contact, in the Estate theme.
 *
 * The details the agency has given us and a form that lands in their own
 * inbox — the same `/crm/public/register-applicant` every other form on
 * the site posts to, so a message here reaches the Listings Manager's
 * enquiry inbox beside everything else rather than going somewhere of its
 * own.
 *
 * `marketing_optin` is false and stays false: somebody asking a question
 * has not asked to be marketed at, and this project's standing rule is
 * that consent is in the query rather than in a filter somebody can
 * forget.
 */
export default function EstateContact({ preview = true }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const { slug: tenantSlug } = useTenantSlug();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  // `brand.address`, not `brand.business` — the chrome's footer reads the
  // same field, and the two disagreeing about where an agency is would be
  // worse than neither showing it.
  const address = addressLine(brand.address);
  const hours = (brand.opening_hours || []).map(hoursLine).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (form.company) return;                       // honeypot
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setSending(true);
    try {
      const [first, ...rest] = form.name.trim().split(/\s+/);
      await api.post("/crm/public/register-applicant", {
        tenant_slug: tenantSlug,
        first_name: first,
        last_name: rest.join(" "),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        notes: form.message.trim(),
        source: "contact_form",
        marketing_optin: false,
        gdpr_consent: true,
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const field = Array.isArray(detail) ? detail[0]?.loc?.slice(-1)[0] : null;
      toast.error(field === "email"
        ? "That email address does not look right. Please check it and try again."
        : "That did not send. Please try again, or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <EstatePage
      preview={preview}
      cta={false}
      title={biz.name ? `Contact ${biz.name}` : undefined}
      description={biz.phone
        ? `Call ${biz.name || "us"} on ${biz.phone}, or send a message and we will come back to you.`
        : undefined}
      eyebrow="Get in touch"
      heading="Contact us"
      lede={biz.tagline || ""}
      crumbs={[{ label: "Contact" }]}
    >
      <div className="contact-grid">
        <Reveal>
          {sent ? (
            <div className="val-card">
              <div className="eyebrow">Thank you</div>
              <h2 className="display-s" style={{ marginTop: 12 }}>Your message is with us.</h2>
              <p style={{ marginTop: 12, color: "var(--ink-2)" }}>
                We will come back to you{biz.phone ? <>, and if it is urgent do call us on{" "}
                  <a className="link" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                    {biz.phone}
                  </a></> : ""}.
              </p>
            </div>
          ) : (
            <form className="val-card" onSubmit={submit}>
              <div className="eyebrow">Send a message</div>
              <div className="form" style={{ marginTop: 22 }}>
                <div className="row">
                  <div className="fld">
                    <label htmlFor="c-name">Your name</label>
                    <input id="c-name" value={form.name} onChange={set("name")} required />
                  </div>
                  <div className="fld">
                    <label htmlFor="c-phone">Telephone</label>
                    <input id="c-phone" type="tel" value={form.phone} onChange={set("phone")} />
                  </div>
                </div>
                <div className="fld">
                  <label htmlFor="c-email">Email</label>
                  <input id="c-email" type="email" value={form.email} onChange={set("email")} required />
                </div>
                <div className="fld">
                  <label htmlFor="c-message">How can we help?</label>
                  <textarea id="c-message" value={form.message} onChange={set("message")} required />
                </div>
                <input value={form.company} onChange={set("company")} tabIndex={-1}
                       autoComplete="off" aria-hidden="true"
                       style={{ position: "absolute", left: "-9999px" }} />
                <button className="btn" type="submit" disabled={sending}>
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          )}
        </Reveal>

        <Reveal>
          <div className="detail-list">
            {biz.phone && (
              <div>
                <div className="k">Telephone</div>
                <a className="link" href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}>
                  {biz.phone}
                </a>
              </div>
            )}
            {biz.email && (
              <div>
                <div className="k">Email</div>
                <a className="link" href={`mailto:${biz.email}`}>{biz.email}</a>
              </div>
            )}
            {address && (
              <div>
                <div className="k">Office</div>
                <div>{address}</div>
              </div>
            )}
            {hours.length > 0 && (
              <div>
                <div className="k">Opening hours</div>
                {hours.map((line) => <div key={line}>{line}</div>)}
              </div>
            )}
            {brand.regions_covered_prose && (
              <div>
                <div className="k">Areas we cover</div>
                <div>{brand.regions_covered_prose}</div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </EstatePage>
  );
}
